import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { CdkStep, CdkStepper } from '@angular/cdk/stepper';
import { StepperHelper } from '@app/_shared/helpers/StepperHelper';
import { AppState } from '@app/core/store/app.state';
import { Observable } from 'rxjs';
import { GetRandomTemplate, SetMarketSectionsIa, SetTemplate, UpdateGeneralData } from '../../store/stepper.actions';
import { StepperState } from '../../store/stepper.state';
import { ImageHelper } from '@app/_shared/helpers/ImageHelper';
import { debounceTime, tap } from 'rxjs/operators';
import {
    getSiteHeaderInfoAction,
    setSiteHeaderInfoAction,
} from '@app/pages/web-site/design/header-customization/store/header-customization.actions';
import { HeaderCustomization } from '@app/_models/pages/design/headerCustomization.model';
import { HeaderCustomizationState } from '@app/pages/web-site/design/header-customization/store/header-customization.state';
import { MODULES_OPTIONS } from '@app/_models/images-manager/images-management.model';
import { TranslateService } from '@ngx-translate/core';
import { SetVertical } from '@app/core/store/app.actions';

@Component({
    selector: 'create-site-ia',
    templateUrl: './create-site-ia.component.html',
    styleUrls: ['./create-site-ia.component.css'],
})
export class CreateSiteAiComponent implements OnInit, OnDestroy {
    @Input() stepName: string;

    InfoMarketForm: UntypedFormGroup;
    submitted: boolean;
    infoMarketData: any;
    urlLogo: string;
    logo: File;
    deleteFileLogo: boolean;
    verticalSelected: string;
    verticalList;

    @Select(StepperState.wizardLogoName)
    wizardLogoName$: Observable<any>;

    @Select(AppState.siteAliases)
    siteAliases$: Observable<string>;

    @Select(AppState.getProfilePhotoTemp)
    profilePhotoTemp$: Observable<string>;

    module: MODULES_OPTIONS = MODULES_OPTIONS.OUT_MODULE;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private cdkStepper: CdkStepper,
        private cdkStep: CdkStep,
        private stepperHelper: StepperHelper,
        private imageHelper: ImageHelper,
        private translate: TranslateService
    ) {
        this.verticalList = [
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.creativo') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.profesional') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.informal') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.divertido') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.respetuoso') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.neutro') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.sofisticado') },
            { name: this.translate.instant('app.wizard.stepper_ia.verticals.seguro') },
        ];
    }

    ngOnInit(): void {
        this.store.dispatch(new getSiteHeaderInfoAction());
        this.store.dispatch(new GetRandomTemplate());
        this.stepperHelper.setCurrentStepName(this.stepName);
        this.stepperHelper.rememberStatus(this.stepName, this.cdkStep);
        this.hasDataLoaded();
        this.buildForm();
        if (this.infoMarketData) {
            if (this.infoMarketData.imagenlogo) {
                this.urlLogo = this.infoMarketData.imagenlogo;
            }
        }
    }

    ngOnDestroy(): void {
        this.stepperHelper.updateStatusStep(this.stepName, this.cdkStep);
    }

    onFormSubmit(): void {
        const formValue = this.InfoMarketForm.value;
        const auxPrompt = 'Tono:' + this.verticalSelected.slice(2) + ' Prompt: ';
        const dataStep = { alias: formValue.alias, imagenlogo: this.urlLogo, prompt: auxPrompt + formValue.prompt };
        localStorage.setItem('userPrompt', auxPrompt + formValue.prompt);
        const randomTemplate = this.store.selectSnapshot(StepperState.randomTemplate);
        this.store.dispatch(new SetTemplate({ id: randomTemplate.template_id }));
        this.store.dispatch(new SetVertical({ vertical: randomTemplate.vertical_id }));
        this.stepperHelper.updateDataStep(this.stepName, dataStep);
        this.stepperHelper.sendStepperStatus();
        this.cdkStepper.next();
    }

    buildForm(): void {
        if (this.infoMarketData) this.urlLogo = this.infoMarketData.imagenlogo;
        const rawPrompt = this.infoMarketData?.prompt ?? '';
        const extractedPrompt = this.extractPromptText(rawPrompt);
        this.verticalSelected = this.extractVerticalSelected(rawPrompt) || '';
        this.InfoMarketForm = this.fb.group({
            alias: [
                this.infoMarketData?.alias ?? '',
                [Validators.required, Validators.maxLength(50), Validators.minLength(2)],
            ],
            prompt: [extractedPrompt, [Validators.required, Validators.minLength(2)]],
        });

        this.marketTitleListenner();
    }

    extractPromptText(input: string): string {
        const match = input?.match(/Prompt:\s*(.*)$/i);
        return match ? match[1].trim() : '';
    }

    extractVerticalSelected(promptText: string): string | null {
        const match = promptText.match(/Tono:\s*([^\n\r:]+?)(?=\s+Prompt:|$)/i);
        if (!match) return null;
        const tone = match[1].trim().toLowerCase();
        const selected =
            this.verticalList.find((item) => {
                const text = item.name || '';
                const withoutEmoji = text
                    .replace(/^[^a-zA-Z0-9]+/, '')
                    .trim()
                    .toLowerCase();
                return withoutEmoji === tone;
            })?.name || null;

        return selected;
    }

    hasDataLoaded(): void {
        this.stepperHelper.getDataStep(this.stepName)
            ? (this.infoMarketData = this.stepperHelper.getDataStep(this.stepName))
            : (this.infoMarketData = null);
    }

    getLogoUrl(url: string): void {
        this.urlLogo = url;
        const uploadedLogo = new FormData();
        uploadedLogo.append('imagenlogo', url);
        uploadedLogo.append('tienelogo', `${1}`);
        uploadedLogo.append(`faviconlogo`, `${1}`);
        uploadedLogo.append(`tienefavicon`, `${1}`);
        this.store.dispatch(new UpdateGeneralData(uploadedLogo)).subscribe({
            next: () => {
                this.deleteFileLogo = false;
                this.loadLogo();
                this.stepperHelper.sendStepperStatus();
            },
        });
    }

    loadLogo(): void {
        const formValue = this.InfoMarketForm.value;
        this.wizardLogoName$.subscribe((value) => {
            this.urlLogo = this.imageHelper.getUrlImage(value);
            const formValue = this.InfoMarketForm.value;
            const auxPrompt = 'Tono:' + this.verticalSelected.slice(2) + ' Prompt: ';
            const dataStep = {
                alias: formValue.alias,
                imagenlogo: this.imageHelper.getUrlImage(value),
                prompt: auxPrompt + formValue.prompt,
            };
            localStorage.setItem('userPrompt', auxPrompt + formValue.prompt);
            this.stepperHelper.updateDataStep(this.stepName, dataStep);
        });
    }

    deleteLogo(): void {
        const formValue = this.InfoMarketForm.value;
        const deleteLogo = new FormData();
        deleteLogo.append('tienelogo', JSON.stringify(0));
        deleteLogo.append(`faviconlogo`, `${0}`);
        deleteLogo.append(`tienefavicon`, `${0}`);
        this.store.dispatch(new UpdateGeneralData(deleteLogo)).subscribe({
            next: () => {
                this.deleteFileLogo = true;
                this.urlLogo = '';
                const auxPrompt = 'Tono:' + this.verticalSelected.slice(2) + ' Prompt: ';
                const dataStep = { alias: formValue.alias, imagenlogo: '', prompt: auxPrompt + formValue.prompt };
                localStorage.setItem('userPrompt', auxPrompt + formValue.prompt);
                this.stepperHelper.updateDataStep(this.stepName, dataStep);
                this.stepperHelper.sendStepperStatus();
            },
        });
    }

    saveMarketName(): void {
        const formValue = this.InfoMarketForm.value;
        const marketTitle = new FormData();
        marketTitle.set('titulo', formValue.alias);
        const auxPrompt = 'Tono:' + this.verticalSelected.slice(2) + ' Prompt: ';
        const dataStep = { alias: formValue.alias, imagenlogo: this.urlLogo, prompt: auxPrompt + formValue.prompt };
        localStorage.setItem('userPrompt', auxPrompt + formValue.prompt);
        this.stepperHelper.updateDataStep(this.stepName, dataStep);
        if (this.InfoMarketForm.valid) {
            let headerCustomization = this.store.selectSnapshot(HeaderCustomizationState).siteHeaderInfo;
            headerCustomization.layoutVersion = 'v2';
            const data: HeaderCustomization = {
                ...headerCustomization,
                selectedLayout: formValue.alias.length > 20 ? '2' : '1',
            };
            this.store
                .dispatch([new UpdateGeneralData(marketTitle), new setSiteHeaderInfoAction(data)])
                .subscribe(() => {
                    this.stepperHelper.sendStepperStatus();
                });
        }
    }

    marketTitleListenner(): void {
        this.InfoMarketForm.valueChanges
            .pipe(
                tap(() => ''),
                debounceTime(700)
            )
            .subscribe(() => {
                this.saveMarketName();
            });
    }

    setVertical(vertical): void {
        this.verticalSelected = vertical;
    }

    backToSelectWizard(): void {
        window.location.reload();
    }
}
